import React, { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Link, useStaticQuery, graphql } from 'gatsby'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

const INTERVAL = 5000 // time each banner stays visible (ms)
const SLIDE_TIME = 600 // slide animation duration (ms)

// Set to your site's locale (e.g. 'en-AU') to use only that locale's content.
// Leave as null to keep the first copy found for each entry.
const LOCALE = null

const Bar = styled.div`
    overflow: hidden;
    background: #fdb515;
    color: #374151;
    font-size: 0.875rem;

    @media (max-width: 640px) {
        font-size: 0.60rem;
    }
`

const Track = styled.div`
    display: flex;
`

const Slide = styled.div`
    flex: 0 0 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.25rem 0.75rem;
    padding: 0.6rem 1rem;
    text-align: center;

    @media (max-width: 640px) {
        flex-wrap: nowrap;
        gap: 0.25rem 0.5rem;
        padding: 0.5rem 0.75rem;
        white-space: nowrap;
    }
`

const Message = styled.span`
    font-weight: 700;

    /* rich text wraps content in <p>; keep it inline */
    p {
        margin: 0;
        display: inline;
    }

    @media (max-width: 640px) {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`

const AnnouncementIcon = styled.span`
    display: inline-block;
    margin-right: 0.25em;
    font-size: 1.5em;
    line-height: 1;
    vertical-align: middle;
`

const Subtext = styled.span`
    opacity: 0.85;

    @media (max-width: 640px) {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`

const linkStyles = `
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem 0.2rem 0.75rem;
    border-radius: 0.375rem;
    background: #374151;
    color: #fff;
    font-size: 0.75em;
    font-weight: 700;
    text-decoration: none;
    &:hover { background: #1f2937; }
    &:focus-visible {
        outline: 2px solid #1f2937;
        outline-offset: 2px;
    }
    @media (max-width: 640px) {
        flex-shrink: 0;
        padding: 0.15rem 0.35rem 0.05rem 0.35rem;
        white-space: nowrap;
    }
`
const ExternalLink = styled.a`${linkStyles}`
const InternalLink = styled(Link)`${linkStyles}`

const isInternal = (url) => /^\/(?!\/)/.test(url)

// Works for Short text (string) and Long text ({ fieldName: string })
const getText = (field) =>
    typeof field === 'string' ? field : field ? Object.values(field)[0] : null

function ActionLink({ url, hidden, children }) {
    const props = { tabIndex: hidden ? -1 : undefined }
    return isInternal(url) ? (
        <InternalLink to={url} {...props}>{children} →</InternalLink>
    ) : (
        <ExternalLink href={url} {...props}>{children} →</ExternalLink>
    )
}

const getActions = (n) =>
    [
        [n.mainActionText, n.mainActionUrl],
        [n.secondActionText, n.secondActionUrl],
        [n.thirdActionText, n.thirdActionUrl],
    ]
        .filter(([text, url]) => text && url)
        .map(([text, url]) => ({ text, url }))

export default function MarqueeBanner() {
    const data = useStaticQuery(graphql`
        query MarqueeBanners {
            allContentfulBanner(filter: { hook: { glob: "Marquee*" } }) {
                nodes {
                    id
                    contentful_id
                    node_locale
                    hook
                    identifier
                    description {
                        json
                    }
                    subtext
                    mainActionText
                    mainActionUrl
                    secondActionText
                    secondActionUrl
                    thirdActionText
                    thirdActionUrl
                }
            }
        }
    `)

    // One banner per Contentful entry (Gatsby returns one node per locale),
    // ordered by identifier: marquee-01, marquee-02, ...
    const nodes = useMemo(() => {
        const seen = new Set()
        return data.allContentfulBanner.nodes
            .filter((n) => !LOCALE || n.node_locale === LOCALE)
            .filter((n) => {
                if (seen.has(n.contentful_id)) return false
                seen.add(n.contentful_id)
                return true
            })
            .sort((a, b) =>
                (a.identifier || a.hook || '').localeCompare(
                    b.identifier || b.hook || '',
                    undefined,
                    { numeric: true }
                )
            )
    }, [data])

    const count = nodes.length

    const [index, setIndex] = useState(0)
    const [animate, setAnimate] = useState(true)
    const [paused, setPaused] = useState(false)
    const [reduced, setReduced] = useState(false)

    useEffect(() => {
        setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    }, [])

    // Advance every INTERVAL
    useEffect(() => {
        if (count < 2 || paused) return
        const id = setInterval(() => {
            setIndex((i) => (reduced ? (i + 1) % count : i >= count ? i : i + 1))
        }, INTERVAL)
        return () => clearInterval(id)
    }, [count, paused, reduced])

    // After sliding onto the cloned first slide, jump back to the real one with no animation
    const handleTransitionEnd = (e) => {
        if (e.target !== e.currentTarget) return
        if (index === count) {
            setAnimate(false)
            setIndex(0)
        }
    }

    // Re-enable the animation once the jump has painted
    useEffect(() => {
        if (animate) return
        const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)))
        return () => cancelAnimationFrame(id)
    }, [animate])

    if (!count) return null

    // Clone the first slide at the end so the loop slides left forever
    const slides = count > 1 ? [...nodes, nodes[0]] : nodes

    return (
        <Bar
            role="region"
            aria-label="Announcements"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <Track
                onTransitionEnd={handleTransitionEnd}
                style={{
                    transform: `translateX(-${index * 100}%)`,
                    transition: animate && !reduced ? `transform ${SLIDE_TIME}ms ease` : 'none',
                }}
            >
                {slides.map((item, i) => {
                    const hidden = i !== index
                    return (
                        <Slide key={`${item.id}-${i}`} aria-hidden={hidden || undefined}>
                            <Message>
                                <AnnouncementIcon aria-hidden="true">📢</AnnouncementIcon>
                                {item.description &&
                                    documentToReactComponents(item.description.json)}
                            </Message>
                            {item.subtext && <Subtext>{getText(item.subtext)}</Subtext>}
                            {getActions(item).map((a) => (
                                <ActionLink key={a.url + a.text} url={a.url} hidden={hidden}>
                                    {a.text}
                                </ActionLink>
                            ))}
                        </Slide>
                    )
                })}
            </Track>
        </Bar>
    )
}